import React, { FunctionComponent, ReactFragment, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector, RootStateOrAny } from 'react-redux'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import 'moment-timezone'

import {
  scheduleCampaign,
  campaignsList,
  campaignResults,
  details,
} from 'Router/routes'
import { campaignScheduleStartEvent } from 'Helpers/tagManager'
import { sendScheduleStartEvent } from 'Redux/actions/googleAnalyticsActions'
import { IconSpotify, RefreshIcon } from 'Stories/Icon/Icon.stories'
import {
  WhiteGreenBtnNarrow,
  WhiteGreenBtn,
  GreenBtn,
} from 'Stories/Button/Button.stories'

import { updateScheduledCampaign } from 'Redux/actions/scheduleActions'
import { createRelaunchCampaign } from 'Redux/actions/campaignActions'
import { CampaignStatus } from 'Constants/enums'
import { formatDate } from 'Constants/formatDate'
import { Mobile, Desktop } from 'Shared/components/Responsive'
import { colors } from 'Styled/variables'
import {
  getAmountOfCampaigns,
  getCampaignToSchedule,
  getCampaigns,
} from 'Redux/selectors/campaigns'
import { getUserSelector } from 'Redux/selectors/auth'
import { CampaignPlatformTypeEnum, CampaignTypeEnum } from 'Types/index'
import {
  getWindowDimensions,
  useWindowDimensions,
} from 'Helpers/useWindowDimensions'
import { activeTabStorage } from 'Constants/constants'

import { CampaignCardProps } from './CampaignCardProps'

import {
  Status,
  StyledLi,
  StyledIcon,
  StyledBtn,
  StyledItem,
  ImgWrapper,
  IconWrapper,
  RoundBtn,
  NamesWrapper,
  Name,
  Artist,
  DateWrapper,
  StyledProgress,
  Text,
  Reviews,
  MobileFieldTitle,
} from './styles'

const CampaignCard: FunctionComponent<CampaignCardProps> = ({
  campaign,
  activeTab,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const campaignDetails = useSelector(getCampaignToSchedule)
  const user = useSelector(getUserSelector)
  const amountOfCampaigns = useSelector(getAmountOfCampaigns)
  const campaigns = useSelector(getCampaigns)

  const history = useHistory()
  const CAMPAIGN_PERIOD = 14
  const { isMobile } = getWindowDimensions()
  useWindowDimensions()

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  const storeActiveTab = () => {
    window.sessionStorage.setItem(activeTabStorage, activeTab)
  }

  const campaignDetailsType = useMemo(
    () => campaigns.find(el => el.id === campaign.id && el.type)?.type,
    [campaigns, campaign.id]
  )

  const campaignIsFeed = useMemo(
    () => campaigns.find(el => el.id === campaign.id && el.is_feed)?.is_feed,
    [campaigns, campaign.id]
  )

  const handleScheduleCampaign = () => {
    storeActiveTab()
    dispatch(updateScheduledCampaign(campaign))

    campaignScheduleStartEvent(
      campaignDetailsType,
      campaignDetails?.genres.map(genre => genre.name).join(),
      campaignDetails?.languages.map(language => language.name).join(),
      campaign.id,
      CampaignPlatformTypeEnum.SPOTIFY,
      user.id,
      amountOfCampaigns,
      campaignIsFeed || false
    )

    dispatch(sendScheduleStartEvent(campaign.id))

    history.push(`${campaignsList}${scheduleCampaign}/${campaign.id}`)
    window.scrollTo(0, 0)
  }

  const handleRelaunchCampaign = (campaign_id: number | string): void => {
    storeActiveTab()
    const url = window.location.href

    dispatch(
      createRelaunchCampaign(campaign_id, url, (error, res) => {
        if (!error) {
          dispatch(updateScheduledCampaign(res))
          dispatch(sendScheduleStartEvent(res.id))

          campaignScheduleStartEvent(
            res.type,
            res?.Track?.TrackGenres?.map(el => el.Genre.genre_name).join(),
            res?.Track?.TrackLanguages?.map(
              el => el.Language.language_name
            ).join(),
            res.id,
            CampaignPlatformTypeEnum.SPOTIFY,
            user.id,
            amountOfCampaigns,
            res?.is_feed || false
          )
          history.push(`/campaigns/schedule/${res.id}`)
        }
        // TODO catch err
      })
    )
  }

  const daysLeft = useMemo(
    () => moment(campaign?.endDate).diff(moment(), 'days') + 1 || 0,
    [campaign]
  )

  const percentageGraph = (daysLeft * 100) / CAMPAIGN_PERIOD

  const handleViewResults = () => {
    storeActiveTab()
    campaign.platformType === CampaignPlatformTypeEnum.SPOTIFY
      ? history.push(`${campaignsList}${campaignResults}/${campaign.id}`)
      : history.push(`${campaignsList}${details}/${campaign.id}`)
  }

  const setPeriod = (stat: CampaignStatus): ReactFragment | null => {
    switch (stat) {
      case CampaignStatus.PENDING_APPROVAL:
      case CampaignStatus.AWAITING_REVIEW:
        return <DateWrapper>{t('campaignsPage.notSetYet')}</DateWrapper>

      case CampaignStatus.APPROVED:
      case CampaignStatus.PENDING:
        return <DateWrapper>{t('campaignsPage.notSetYet')}</DateWrapper>

      case CampaignStatus.SCHEDULED:
        return (
          <DateWrapper>
            {moment
              .tz(campaign.startDate, timezone)
              .format(formatDate.formatDateMonthFull)}
            &nbsp;-&nbsp;
            {moment
              .tz(campaign.endDate, timezone)
              .format(formatDate.formatDateMonthFull)}
          </DateWrapper>
        )

      case CampaignStatus.RUNNING:
      case CampaignStatus.INPROCESS:
        return (
          <DateWrapper>
            {moment
              .tz(campaign.startDate, timezone)
              .format(formatDate.formatDateMonthFull)}
            ,&nbsp;{daysLeft}&nbsp;
            {daysLeft === 1
              ? t('campaignsPage.dayLeft')
              : t('campaignsPage.daysLeft')}
            &nbsp;
            <StyledProgress
              percent={percentageGraph}
              steps={14}
              width={176}
              strokeWidth={4}
              showInfo={false}
              strokeColor={
                daysLeft >= 4 ? colors.green : colors.messegesYellow1
              }
              trailColor={colors.greyTextBlackBg}
              status="active"
            />
          </DateWrapper>
        )

      case CampaignStatus.ENDED:
      case CampaignStatus.FINISHED:
        return (
          <>
            <DateWrapper>
              {moment
                .tz(campaign.startDate, timezone)
                .format(formatDate.formatDateMonthFull)}
              &nbsp;-&nbsp;
              {moment
                .tz(campaign.endDate, timezone)
                .format(formatDate.formatDateMonthFull)}
            </DateWrapper>
          </>
        )
      case CampaignStatus.STOPPED:
        return (
          <>
            {Boolean(campaign.startDate) && Boolean(campaign.endDate) ? (
              <DateWrapper>
                {moment
                  .tz(campaign?.startDate, timezone)
                  .format(formatDate.formatDateMonthFull)}
                &nbsp;-&nbsp;
                {moment
                  .tz(campaign?.endDate, timezone)
                  .format(formatDate.formatDateMonthFull)}
              </DateWrapper>
            ) : (
              <DateWrapper>{t('campaignsPage.stoppedByAdmin')}</DateWrapper>
            )}
          </>
        )

      case CampaignStatus.PAYMENT_PENDING:
        return <DateWrapper>{t('campaignsPage.notSetYet')}</DateWrapper>
      default:
        return null
    }
  }

  const setStatus = (stat: CampaignStatus): ReactFragment | null => {
    switch (stat) {
      case CampaignStatus.PENDING_APPROVAL:
      case CampaignStatus.AWAITING_REVIEW:
        return t('campaignsPage.pendingApproval')

      case CampaignStatus.APPROVED:
      case CampaignStatus.PENDING:
        return t('campaignsPage.approved')

      case CampaignStatus.SCHEDULED:
        return t('campaignsPage.scheduled')

      case CampaignStatus.RUNNING:
      case CampaignStatus.INPROCESS:
        return (
          <div>
            <div>{t('campaignsPage.running')}</div>

            <Reviews>
              {`${campaign.reviewedReviews || 0}${
                !campaign.is_feed ? `/` : ``
              }${!campaign.is_feed ? campaign.allReview || 0 : ``}`}
              &nbsp;
              {t('campaignsPage.amountReviews')}
            </Reviews>
          </div>
        )

      case CampaignStatus.ENDED:
      case CampaignStatus.FINISHED:
        return (
          <div>
            <div>{t('campaignsPage.ended')}</div>

            <Reviews>
              {`${campaign.reviewedReviews || 0}${!campaign.is_feed && `/`}${
                !campaign.is_feed ? campaign.allReview || 0 : ``
              }`}
              &nbsp;
              {t('campaignsPage.amountReviews')}
            </Reviews>
          </div>
        )

      case CampaignStatus.STOPPED:
        return (
          <div>
            <div>{t('campaignsPage.stopped')}</div>
          </div>
        )
      case CampaignStatus.PAYMENT_PENDING:
        return t('campaignsPage.pendingPayment')
      default:
        return null
    }
  }

  const setButton = (stat: CampaignStatus): ReactFragment | null => {
    switch (stat) {
      case CampaignStatus.PENDING_APPROVAL:
      case CampaignStatus.AWAITING_REVIEW:
        return <Text>{t('campaignsPage.teamWillReview')}</Text>

      case CampaignStatus.APPROVED:
      case CampaignStatus.PENDING:
        return (
          <StyledBtn>
            <GreenBtn
              color={isMobile ? 'greenMoreNarrow' : 'greenNarrow'}
              onClick={handleScheduleCampaign}
            >
              {t('campaignsPage.startCampaign')}
            </GreenBtn>

            {/* TODO temporary committed for TikTok release */}
            {/* <div>
              <StyledPopover>
                <GlobalStyled />
                <Popover
                  overlayClassName="overlayClassName"
                  overlayInnerStyle={{ borderRadius: '8px' }}
                  visible={visible}
                  trigger="click"
                  content={
                    <Wrapper>
                      <StyledTitle>
                        {t('campaignsPage.areYouSureYouWantToDelete')}
                      </StyledTitle>
                      <BtnsWrapper>
                        <DottedBtn
                          content={t('campaignsPage.cancel')}
                          onClick={togglePopover}
                        />
                        <div>
                          <GreenBtn
                            color="green"
                            onClick={() =>
                              // TODO after backend part
                              // eslint-disable-next-line no-console
                              console.log('Will be done after backend part')
                            }
                          >
                            {t('campaignsPage.yes')}
                          </GreenBtn>
                        </div>
                      </BtnsWrapper>
                    </Wrapper>
                  }
                >
                  <RoundBtn onClick={togglePopover}>
                    <RubbishIcon />
                  </RoundBtn>
                </Popover>
              </StyledPopover>
            </div> */}
          </StyledBtn>
        )

      case CampaignStatus.SCHEDULED:
        return (
          <Text>
            {t('campaignsPage.campaignWillStart')}&nbsp;
            {moment.tz(campaign.startDate, timezone).format('MM/DD/YYYY')}
          </Text>
        )

      case CampaignStatus.RUNNING:
      case CampaignStatus.INPROCESS:
        return (
          <StyledBtn>
            <WhiteGreenBtn
              color={isMobile ? 'whiteGreenMoreNarrow' : 'whiteGreenMoreWide'}
              onClick={handleViewResults}
            >
              {t('campaignsPage.details')}
            </WhiteGreenBtn>
          </StyledBtn>
        )

      case CampaignStatus.ENDED:
      case CampaignStatus.FINISHED:
        return (
          <StyledBtn>
            <Mobile>
              <WhiteGreenBtn
                color="whiteGreenMoreNarrow"
                onClick={handleViewResults}
              >
                {t('campaignsPage.details')}
              </WhiteGreenBtn>
            </Mobile>

            <Desktop>
              <WhiteGreenBtnNarrow
                color="whiteGreenMoreWide"
                onClick={handleViewResults}
              >
                {t('campaignsPage.details')}
              </WhiteGreenBtnNarrow>
            </Desktop>

            {campaign.platformType === CampaignPlatformTypeEnum.SPOTIFY &&
              !campaign.relaunched && (
                <RoundBtn
                  onClick={() => {
                    handleRelaunchCampaign(campaign.id)
                  }}
                >
                  <RefreshIcon />
                </RoundBtn>
              )}
          </StyledBtn>
        )

      case CampaignStatus.STOPPED:
        return (
          <StyledBtn>
            <Mobile>
              <WhiteGreenBtn
                color="whiteGreenMoreNarrow"
                onClick={handleViewResults}
              >
                {t('campaignsPage.details')}
              </WhiteGreenBtn>
            </Mobile>

            <Desktop>
              <WhiteGreenBtnNarrow
                color="whiteGreenMoreWide"
                onClick={handleViewResults}
              >
                {t('campaignsPage.details')}
              </WhiteGreenBtnNarrow>
            </Desktop>

            {campaign.platformType === CampaignPlatformTypeEnum.SPOTIFY &&
              !campaign.relaunched && (
                <RoundBtn
                  onClick={() => {
                    handleRelaunchCampaign(campaign.id)
                  }}
                >
                  <RefreshIcon />
                </RoundBtn>
              )}
          </StyledBtn>
        )

      case CampaignStatus.PAYMENT_PENDING:
        return t('campaignsPage.pendingPayment')
      default:
        return null
    }
  }

  return (
    <>
      <StyledLi status={campaign.status}>
        <StyledIcon>
          <ImgWrapper>
            <img alt="" width="40" src={campaign.trackCoverUrl} />
          </ImgWrapper>

          <IconWrapper>
            <IconSpotify />
          </IconWrapper>
        </StyledIcon>

        <NamesWrapper>
          <div>
            <MobileFieldTitle>Track name</MobileFieldTitle>
            <Name>{campaign.trackTitle}</Name>
          </div>
          <div>
            <MobileFieldTitle>Artist</MobileFieldTitle>
            <Artist>{campaign.trackAuthorName}</Artist>
          </div>
        </NamesWrapper>

        <div>
          <MobileFieldTitle>Time period</MobileFieldTitle>
          {setPeriod(campaign.status)}
        </div>

        <StyledItem>
          <MobileFieldTitle>Status</MobileFieldTitle>
          <Status status={campaign.status}>{setStatus(campaign.status)}</Status>
        </StyledItem>

        {setButton(campaign.status)}
      </StyledLi>
    </>
  )
}

export default CampaignCard
